// ─────────────────────────────────────────────────────────────────────────────
// DomainDashboardScreen — domain landing host (Chunk 6 §4-5).
//
// Renders inside AppShell's content area. Layout:
//   • domain title with domain-coloured underline (lib/agents.ts accent)
//   • agent card grid:
//       - domain-native agents
//       - SERVICE_AGENTS always visible (Flag 7)
//       - CODE_TEAM cross-domain except [CODE,FRONTIER,RESEARCH,SECURITY] (Flag 7)
//   • body slot:
//       - CODE      → <CodePage authToken onBack/>     (Q4-b; internals untouched per Flag 6)
//       - SECURITY  → <SecurityPage authToken onBack/> (Q4-c; internals untouched per Flag 6)
//       - else      → domain-specific placeholder body
//
// Per Ghost FIX 2: authToken + onBack are explicit props supplied by App.tsx for
// the inline CodePage / SecurityPage mounts. Per Q10 + Flag 10: `onBack` here is
// `AppShell.onBackPress`, which steps `domain → home` — no backend POST.
//
// Per Ghost Flag 8: card click calls onAgentClick(id) which composes
// `await handleAgentChange(id)` + `setScreen('agent')` in App.tsx (the existing
// async handler — POSTs /api/agent + clears + loads threads — is awaited first).
// ─────────────────────────────────────────────────────────────────────────────

import React, { Suspense, useState } from 'react';
import {
  AgentDef,
  CODE_TEAM,
  CODE_TEAM_EXCLUDED_DOMAINS,
  SERVICE_AGENTS,
  getDomain,
} from '../../lib/agents';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import CodePage from '../CodePage';
import SecurityPage from '../SecurityPage';
import DomainDashboard from '../dashboards/DomainDashboard';
import MissionControl from '../missioncontrol/MissionControl';
import BucklyOverviewCard from '../buckly/BucklyOverviewCard';
import type { Nav } from '../missioncontrol/navigation';

// ─────────────────────────────────────────────────────────────────────────────
// TRADING VIEWS — owner ruling 15 Oct: "In between overview and Arthur, there
// should be a globe tab… Call it global." The desktop draws no Overview tab, so
// (owner's choice, same day) two VIEW tiles lead TRADING's card row: OVERVIEW is
// today's body, unchanged; GLOBAL swaps the body for the globe.
//
// THEY ARE NOT AGENTS. They live here, not in lib/agents.ts: never in the roster,
// never through onAgentClick, never reaching agent pages, chat routing or the
// call path. The globe is lazy, so Trading Overview downloads none of Cesium.
// ─────────────────────────────────────────────────────────────────────────────
const TradingGlobal = React.lazy(() => import('../trading/global/TradingGlobal'));
type TradingView = 'overview' | 'global';
const TRADING_VIEWS: { id: TradingView; label: string }[] = [
  { id: 'overview', label: 'OVERVIEW' },
  { id: 'global', label: 'GLOBAL' },
];

const MONO = REXY_FONT;

// Matches existing AgentSidebar.tsx code-team styling (purple-400 active state).
const CODE_TEAM_ACCENT = '#c084fc';
// Service agents use the homepage console cyan — distinguishes "always-available
// service tier" from per-domain agents which carry the domain's own accent.
const SERVICE_ACCENT = REXY_CONSOLE.primary;

// ─────────────────────────────────────────────────────────────────────────────
// FULL-SURFACE DOMAINS — a domain whose body owns the ENTIRE screen.
//
// The generic screen wraps every body in shared chrome: a domain title with an
// accent underline, then a grid of agent cards. A domain that DECLARES a root
// vocabulary brings its own chrome — brand block, page title, root tabs and a
// three-group rail whose roster IS the agent affordance — so the shared chrome
// would render a second title and a second agent row above it.
//
// This is a CAPABILITY, not a domain name repeated. GROWTH is its only member
// today; the next domain that declares roots joins this set and needs no edit to
// the surface itself. The five domains on the generic route fall through the
// identical branch below, byte-identical to before.
//
// FLAGGED, NOT RESOLVED: the shared grid also carries the cross-domain SERVICE
// and CODE-team cards. The design's rail is the domain's OWN roster and has no
// group for them, so on a full-surface domain they are not reached from here.
// That is the owner's call — a fourth rail group is one constant — and it is
// surfaced rather than silently kept or silently dropped.
export const FULL_SURFACE_DOMAINS: readonly string[] = ['GROWTH'];

/** On a full-surface domain an agent's dashboard IS its Mission Control root, so
 *  there is no second dashboard to render. Exported so App.tsx can route the
 *  agent view's DASHBOARD tab back here instead of mounting a placeholder. */
export const dashboardIsMissionControl = (domain: string) =>
  FULL_SURFACE_DOMAINS.includes(domain);

function renderFullSurface(
  domain: string, authToken: string | null,
  activeAgent: string | null, onAgentClick: (agent: string) => void,
  nav?: Nav, onNavChange?: (n: Nav) => void,
): React.ReactNode {
  switch (domain) {
    case 'GROWTH':
      return (
        <MissionControl
          domain={domain}
          authToken={authToken}
          activeAgent={activeAgent}
          onAgentClick={onAgentClick}
          nav={nav}
          onNavChange={onNavChange}
        />
      );
    default:
      return null;
  }
}

function renderBody(domain: string, authToken: string | null, onBack: () => void): React.ReactNode {
  switch (domain) {
    case 'CODE':
      return <CodePage authToken={authToken} onBack={onBack} />;
    case 'SECURITY':
      return <SecurityPage authToken={authToken} onBack={onBack} />;
    // Stage 3 — the generic real dashboard (domain = sum of its agents). Renders
    // the real Stage-1 tier always; reportable domains add session content,
    // non-reportable (RESEARCH/FRONTIER) show tier/status only.
    case 'PERSONAL':
    case 'TRADING':
    case 'BUSINESS':
    case 'RESEARCH':
    case 'FRONTIER':
    case 'MEDIA':
      return <DomainDashboard domain={domain} authToken={authToken} />;
    default:
      return null;
  }
}

export interface DomainDashboardScreenProps {
  domain: string;
  activeAgent: string | null;
  onAgentClick: (agent: string) => void;
  // FIX 2 — explicit (required for inline-mounted CodePage / SecurityPage):
  authToken: string | null;
  onBack: () => void; // === AppShell's onBackPress (steps domain → home, Q10)
  /** Mission Control's position, owned by App so it survives the chat round trip. */
  nav?: Nav;
  onNavChange?: (n: Nav) => void;
}

export default function DomainDashboardScreen({
  domain,
  activeAgent,
  onAgentClick,
  authToken,
  onBack,
  nav,
  onNavChange,
}: DomainDashboardScreenProps) {
  const [tradingView, setTradingView] = useState<TradingView>('overview');
  const d = getDomain(domain);
  if (!d) return null;

  // A full-surface domain brings its own chrome — render it alone. The wrapper
  // keeps ONLY the padding, because AppShell's fixed back chevron (top-left) and
  // corner orb (top-right) sit at top:14 and must not be rendered under.
  if (FULL_SURFACE_DOMAINS.includes(domain)) {
    return (
      <div style={fullSurfaceStyle}>
        {renderFullSurface(domain, authToken, activeAgent, onAgentClick, nav, onNavChange)}
      </div>
    );
  }

  // Flag 7: code team surfaced cross-domain except for excluded domains.
  const codeTeamVisible = !(CODE_TEAM_EXCLUDED_DOMAINS as readonly string[]).includes(domain);

  return (
    <div style={rootStyle}>
      {/* Domain title with domain-coloured underline (spec §4) */}
      <div style={titleRowStyle}>
        <span
          style={{
            ...titleTextStyle,
            borderBottom: `2px solid ${d.accent}`,
          }}
        >
          {d.label}
        </span>
      </div>

      {/* Agent card grid */}
      <div style={cardsGridStyle}>
        {domain === 'TRADING' &&
          TRADING_VIEWS.map((v) => (
            <ViewTile
              key={`view-${v.id}`}
              label={v.label}
              active={tradingView === v.id}
              accent={d.accent}
              onClick={() => setTradingView(v.id)}
            />
          ))}

        {d.agents.map((agent) => (
          // On the Trading OVERVIEW, Buckly's card is the design's own (Trading
          // Homepage.dc.html:111-125, owner ruling U6); same click, same route. On
          // GLOBAL her tile stays as it was, so the globe keeps its full height.
          domain === 'TRADING' && tradingView === 'overview' && agent.id === 'buckly' ? (
            <BucklyOverviewCard key={agent.id} onClick={() => onAgentClick(agent.id)} />
          ) : (
          <AgentCard
            key={agent.id}
            agent={agent}
            active={activeAgent === agent.id}
            accent={d.accent}
            onClick={() => onAgentClick(agent.id)}
          />
          )
        ))}

        {SERVICE_AGENTS.map((agent) => (
          <AgentCard
            key={`svc-${agent.id}`}
            agent={agent}
            active={activeAgent === agent.id}
            accent={SERVICE_ACCENT}
            onClick={() => onAgentClick(agent.id)}
            badge="SERVICE"
          />
        ))}

        {codeTeamVisible &&
          CODE_TEAM.map((agent) => (
            <AgentCard
              key={`code-${agent.id}`}
              agent={agent}
              active={activeAgent === agent.id}
              accent={CODE_TEAM_ACCENT}
              onClick={() => onAgentClick(agent.id)}
              badge="CODE"
            />
          ))}
      </div>

      {/* Body slot — CodePage / SecurityPage / placeholder */}
      <div style={bodySlotStyle}>
        {domain === 'TRADING' && tradingView === 'global' ? (
          <Suspense fallback={<div style={globeLoadingStyle}>loading the globe engine — fetched once, on first open</div>}>
            <TradingGlobal />
          </Suspense>
        ) : (
          renderBody(domain, authToken, onBack)
        )}
      </div>
    </div>
  );
}

/** A Trading VIEW tile — the agent card's look, with no agent behind it. */
function ViewTile({ label, active, accent, onClick }: {
  label: string; active: boolean; accent: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        ...cardStyle,
        borderColor: active ? accent : 'rgba(255,255,255,0.12)',
        color: active ? accent : 'rgba(255,255,255,0.65)',
        background: active ? 'rgba(8,8,24,0.65)' : 'rgba(8,8,24,0.45)',
      }}
    >
      <div style={cardLabelStyle}>{label}</div>
      <div style={cardBadgeStyle}>VIEW</div>
    </button>
  );
}

function AgentCard({
  agent,
  active,
  accent,
  onClick,
  badge,
}: {
  agent: AgentDef;
  active: boolean;
  accent: string;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cardStyle,
        borderColor: active ? accent : 'rgba(255,255,255,0.12)',
        color: active ? accent : 'rgba(255,255,255,0.65)',
        background: active ? 'rgba(8,8,24,0.65)' : 'rgba(8,8,24,0.45)',
      }}
    >
      <div style={cardLabelStyle}>{agent.label}</div>
      {badge && <div style={cardBadgeStyle}>{badge}</div>}
    </button>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────
// Top padding clears AppShell's fixed back chevron (top-left, 36 px) and corner
// orb (top-right, 64 px), both at top:14 — so 90 px is a safe floor.
const rootStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  boxSizing: 'border-box',
  padding: '90px 32px 24px 32px',
  color: REXY_CONSOLE.primary,
  fontFamily: MONO,
};

// Same 90 px top clearance as rootStyle — AppShell's fixed chevron and corner
// orb sit above every screen. The surface supplies its own background and type.
const fullSurfaceStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  padding: '90px 32px 20px 32px',
  minHeight: 0,
};

const titleRowStyle: React.CSSProperties = {
  marginBottom: 28,
};

const titleTextStyle: React.CSSProperties = {
  fontFamily: MONO,
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: 6,
  color: '#ffffff',
  paddingBottom: 6,
};

const cardsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: 10,
  marginBottom: 28,
};

const cardStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderRadius: 8,
  border: '1px solid',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: MONO,
  transition: 'border-color 200ms, color 200ms, background 200ms',
};

const cardLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 2,
};

const cardBadgeStyle: React.CSSProperties = {
  fontSize: 8,
  letterSpacing: 1.5,
  marginTop: 4,
  opacity: 0.6,
};

const globeLoadingStyle: React.CSSProperties = {
  padding: 24,
  fontSize: 11,
  letterSpacing: 1.5,
  color: 'rgba(255,255,255,0.5)',
};

const bodySlotStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};
